import React, { useMemo, useEffect } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Flex,
  Container,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Button,
  Progress,
  Grid,
  GridItem,
  Heading,
  Center,
  Skeleton,
  MenuButton,
  Menu,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  useToast,
} from "@chakra-ui/react";
import { GrLineChart } from "react-icons/gr";
import AdminDataTable from "components/AdminDataTable/AdminDataTable";
import UpgradeUserModal, { type UpgradeUserPayload } from "components/UpgradeUserModal";
import { AiOutlineSearch } from "react-icons/ai";
import { userAnalyticRequest } from "services/request";
import { QUERY_KEYS } from "utils/constants";
import dayjs from "dayjs";
import Paginator from "components/Paginator";
import { useQuery } from "react-query";
import axiosInstance from "services/httpService";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { BiFilter } from "react-icons/bi";
import { FaSortAlphaDownAlt, FaSortAlphaUpAlt } from "react-icons/fa";

const RegisteredUsers = () => {
  const history = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [qParams, setQParams] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [dateIndex, setDateIndex] = React.useState(0);
  const [dateFilter, setDateFilter] = React.useState("allTime");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [sortValue, setSortValue] = React.useState("userName");
  const [order, setOrder] = React.useState("asc");
  const [upgradeUserEmail, setUpgradeUserEmail] = React.useState("");
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);

  const makeAPICall = async ({ queryKey }) => {
    const q = queryKey[1];
    const page = queryKey[2];
    const size = queryKey[3];
    const search = queryKey[4];
    const date = queryKey[5];
    const sortBy = queryKey[6];
    const order = queryKey[7];
    const url =
      userAnalyticRequest.USERS_ANALYTIC +
      `?q=${q}&page=${page}&size=${size}&search=${search}&date=${date}&sortBy=${sortBy}&order=${order}`;
    const res = await axiosInstance.get(url);
    return res.data;
  };

  const {
    data: userDetails = {},
    isLoading,
    refetch,
  } = useQuery(
    [
      QUERY_KEYS.USER_ANALYTICS,
      qParams,
      currentPage,
      pageSize,
      search,
      dateFilter,
      sortValue,
      order,
    ],
    makeAPICall,
  );

  const summary = useMemo(() => userDetails?.summary, [userDetails]);

  useEffect(() => {
    if (startDate.length === 10 && endDate.length === 10) {
      const concatenatedDate = startDate + "," + endDate;
      setDateFilter(concatenatedDate);
      setDateIndex(10);
      handleDateFilter(dateFilter);
    }
  }, [startDate, endDate]);

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", String(newPage));
  };

  const handleSizeChange = (newSize) => {
    searchParams.set("size", String(newSize));
  };

  const handleQueryChange = (newQuery) => {
    searchParams.set("q", String(newQuery));
  };

  const handleSearch = (newSearch) => {
    searchParams.set("search", String(newSearch));
  };

  const handleDateFilter = (newDateRange) => {
    searchParams.set("date", String(newDateRange));
  };

  const handleSortChange = (newValue) => {
    if (newValue === "startDate" || newValue === "daysLeft") {
      newValue = "userName";
    }
    setSortValue(newValue);
  };

  const handleOrderChange = (newValue) => {
    setOrder(newValue);
  };

  const openUpgradeModal = (email: string) => {
    setUpgradeUserEmail(email);
    setIsUpgradeModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
    setUpgradeUserEmail("");
  };

  const handleUpgradeComplete = (upgradeData: UpgradeUserPayload) => {
    toast({
      title: "User Upgraded",
      description: `User ${upgradeData.email} has been successfully upgraded.`,
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    void refetch();
  };

  const { paginationMeta } = userDetails;
  const Users = userDetails?.data;

  if (userDetails.data?.length === 0 && paginationMeta?.totalPages < currentPage) {
    setCurrentPage(0);
    handlePageChange(currentPage);
  }

  const COLUMNS = [
    {
      Header: "User Name",
      accessor: "userName",
    },
    {
      Header: "Email",
      accessor: "email",
    },

    {
      Header: "Registered On",
      Cell: function uploaded({ cell }: any) {
        return <div> {dayjs(cell.row.original.createdAt).format("MMM D YYYY")}</div>;
      },
    },
    {
      Header: "Last Log In",
      Cell: function uploaded({ cell }: any) {
        return (
          <div>
            {" "}
            {!cell.row.original.lastLoginAt
              ? "N/A"
              : dayjs(cell.row.original.lastLoginAt).format("MMM D YYYY")}
          </div>
        );
      },
    },
    {
      Header: "Status",
      accessor: "isVerified",
    },
    {
      Header: "Action",
      Cell: function uploaded({ cell }: any) {
        return (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={(event) => {
              event.stopPropagation();
              openUpgradeModal(cell.row.original.email);
            }}
          >
            Upgrade
          </Button>
        );
      },
    },
  ];

  const SUBSCRIPTIONCOLUMN = [
    {
      Header: "User Name",
      accessor: "userName",
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Registered On",
      Cell: function uploaded({ cell }: any) {
        return <div> {dayjs(cell.row.original.createdAt).format("MMM D YYYY")}</div>;
      },
    },
    {
      Header: "Subscribed On",
      Cell: function uploaded({ cell }: any) {
        return <div> {dayjs(cell.row.original?.subscription?.startDate).format("MMM D YYYY")}</div>;
      },
    },
    {
      Header: "Subscription Plan",
      Cell: function uploaded({ cell }: any) {
        return <div> {cell.row.original?.subscription?.plan?.name}</div>;
      },
    },
    {
      Header: "Expiration Date",
      Cell: function uploaded({ cell }: any) {
        return <div> {dayjs(cell.row.original?.subscription?.endDate).format("MMM D YYYY")}</div>;
      },
    },
    // {
    //   Header: "Number of Days Left",
    //   Cell: function uploaded({ cell }: any) {
    //     return (
    //       <div>
    //         {" "}
    //         {dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
    //           (1000 * 60 * 60 * 24) <=
    //         0 ? (
    //           0
    //         ) : (
    //           <div>
    //             {dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
    //               (1000 * 60 * 60 * 24) >
    //             50 ? (
    //               <div>
    //                 {Math.round(
    //                   dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
    //                     (1000 * 60 * 60 * 24 * 30),
    //                 )}
    //                 <span> Months</span>
    //               </div>
    //             ) : (
    //               <div>
    //                 {Math.round(
    //                   dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
    //                     (1000 * 60 * 60 * 24),
    //                 )}
    //                 <span>
    //                   {Math.round(
    //                     dayjs(cell.row.original?.subscription?.endDate).diff(dayjs()) /
    //                       (1000 * 60 * 60 * 24),
    //                   ) === 1 ? (
    //                     <span> Day</span>
    //                   ) : (
    //                     <span> Days</span>
    //                   )}
    //                 </span>
    //               </div>
    //             )}
    //           </div>
    //         )}
    //       </div>
    //     );
    //   },
    // },

    {
      Header: "Status",
      Cell: function uploaded({ cell }: any) {
        const daysLeft = dayjs(cell.row.original?.subscription?.endDate).diff(dayjs(), "day");

        if (daysLeft > 0) {
          return <div>ACTIVE</div>;
        } else {
          return <div>INACTIVE</div>;
        }
      },
    },
    {
      Header: "Action",
      Cell: function uploaded({ cell }: any) {
        return (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={(event) => {
              event.stopPropagation();
              openUpgradeModal(cell.row.original.email);
            }}
          >
            Upgrade
          </Button>
        );
      },
    },
  ];

  const items = [
    {
      title: "Registered Users",
      number: summary?.allUsers,
      text: "",
      percentage: Math.round((summary?.allUsers / summary?.allUsers) * 100),
      icon: "percentage",
      color: "blue",
      bgColor: "blue.100",
    },
    {
      title: "Verified Users",
      number: summary?.verifiedUsers,
      percentage: Math.round((summary?.verifiedUsers / summary?.allUsers) * 100),
      text: "verified",
      color: "pink",
      bgColor: "pink.100",
    },
    {
      title: "Subscribed Users",
      number: summary?.subscribed,
      percentage: Math.round((summary?.subscribed / summary?.allUsers) * 100),
      text: "subscribed",
      color: "orange",
      bgColor: "orange.100",
    },
  ];

  const dateFilterData = [
    {
      name: "All Time",
      value: "allTime",
    },
    {
      name: "This Year",
      value: "thisYear",
    },
    {
      name: "This Month",
      value: "thisMonth",
    },
    {
      name: "This Week",
      value: "thisWeek",
    },
    {
      name: "Today",
      value: "today",
    },
  ];

  return (
    <Container mt="4" maxW="container.xl">
      <Flex justify={"space-between"} mt={"20px"} align="center">
        <Button
          variant="secondary"
          onClick={() => {
            history(-1);
          }}
        >
          Back
        </Button>

        <Text fontSize="20px" fontWeight="500">
          All User Category
        </Text>

        <Box
          display={{
            base: "none",
            lg: "block",
            xl: "block",
          }}
        >
          <InputGroup>
            <InputRightElement pointerEvents="none">
              <AiOutlineSearch color="gray.300" />
            </InputRightElement>
            <Input
              rounded="full"
              type="text"
              placeholder="Search"
              bg="white"
              onChange={(e) => {
                setSearch(e.target.value);
                handleSearch(search);
              }}
            />
          </InputGroup>
        </Box>
      </Flex>

      <Box mt="2rem" display="flex" alignItems="center" gap={5}>
        <Flex border="1px solid #E0E6DD" display="inline-flex" alignItems="stretch" height="42px">
          {dateFilterData.map((item, index: number) => (
            <Button
              key={index}
              _hover={{ border: "1px solid #005aff" }}
              bg={dateIndex === index ? "#1f74cf" : "white"}
              p="20px 20px"
              mb={{
                base: "10px",
                md: "0",
                lg: "0",
              }}
              cursor={dateIndex === index ? "default" : "pointer"}
              pointerEvents={dateIndex === index ? "none" : "auto"}
              color={dateIndex === index ? "white" : "black"}
              onClick={() => {
                setDateFilter(item.value);
                setDateIndex(index);
                setStartDate("");
                setEndDate("");
                handleDateFilter(dateFilter);
              }}
            >
              {item.name}
            </Button>
          ))}
        </Flex>
        <Flex width="19rem" gap={2} cursor={"pointer"} color={dateIndex === 10 ? "white" : "black"}>
          <Input
            height="42.5px"
            placeholder={"Select Start Date"}
            size="sm"
            color={dateIndex === 10 ? "white" : "black"}
            bg={dateIndex === 10 ? "#1f74cf" : "white"}
            borderRadius={dateIndex === 10 ? "md" : ""}
            type={"date"}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
          />
          <Center color="black" pointerEvents="none">
            <ArrowForwardIcon pointerEvents="none" />
          </Center>
          <Input
            height="42.5px"
            placeholder={"Select Start Date"}
            size="sm"
            color={dateIndex === 10 ? "white" : "black"}
            bg={dateIndex === 10 ? "#1f74cf" : "white"}
            borderRadius={dateIndex === 10 ? "md" : ""}
            type={"date"}
            onChange={(e) => {
              setEndDate(e.target.value);
            }}
          />
        </Flex>
      </Box>

      <Flex width="19rem" gap={2} cursor={"pointer"} color={"black"}>
        <Box textAlign="left">
          <Menu>
            <MenuButton
              mt="1rem"
              mb="2rem"
              as={Button}
              aria-label="Sort scripts"
              leftIcon={<BiFilter />}
              variant="outline"
              fontWeight="normal"
            >
              Sort By:{" "}
              {sortValue === "userName"
                ? "User Name"
                : sortValue === "email"
                ? "Email"
                : sortValue === "startDate"
                ? "Start Date"
                : "Days Left"}{" "}
            </MenuButton>
            <MenuList minWidth="240px" zIndex={14}>
              <MenuOptionGroup
                onChange={handleSortChange}
                defaultValue="userName"
                title="Sort"
                type="radio"
              >
                <MenuItemOption value="userName">User Name</MenuItemOption>
                <MenuItemOption value="email">Email</MenuItemOption>
                <MenuItemOption value="startDate">Start Date</MenuItemOption>
                <MenuItemOption value="daysLeft">Days Left</MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </Box>
        <Box textAlign="left">
          <Menu>
            <MenuButton
              mt="1rem"
              mb="2rem"
              as={Button}
              aria-label="Sort scripts"
              leftIcon={order === "asc" ? <FaSortAlphaUpAlt /> : <FaSortAlphaDownAlt />}
              variant="outline"
              fontWeight="normal"
            >
              Order: {order === "asc" ? "Ascending" : "Descending"}{" "}
            </MenuButton>
            <MenuList minWidth="240px" zIndex={14}>
              <MenuOptionGroup
                onChange={handleOrderChange}
                defaultValue="asc"
                title="Order"
                type="radio"
              >
                <MenuItemOption value="asc">Ascending</MenuItemOption>
                <MenuItemOption value="desc">Descending</MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </Box>
      </Flex>

      <Grid
        gridTemplateColumns={{
          md: "repeat(2, 1fr)",

          xl: "repeat(4, 1fr)",
        }}
        display={{
          base: "block",
          md: "grid",
          xl: "grid",
        }}
        mt="20px"
        gridGap="24px"
      >
        {items?.map((item, index: number) => {
          return (
            <GridItem
              _hover={{ border: `2px solid ${item.color}` }}
              key={index}
              p="30px 20px"
              mb={{
                base: "10px",
                md: "0",
                lg: "0",
              }}
              bg={item.bgColor}
              borderRadius="10px"
              border={currentIndex === index ? `4px solid ${item?.color}` : "2px solid #E0E6DD"}
              cursor={currentIndex === index ? "default" : "pointer"}
              pointerEvents={currentIndex === index ? "none" : "auto"}
              color="black"
              onClick={() => {
                setCurrentIndex(index);
                setQParams(item?.text);
                handleQueryChange(qParams);
              }}
            >
              <Box>
                <Flex justifyContent="space-between" mt="-20px">
                  <Text fontSize="20px" fontWeight="800">
                    {item?.title}
                  </Text>
                </Flex>

                <Flex justifyContent="space-between" align="center" mt="20px">
                  <Flex
                    bg={item.color}
                    borderRadius="15px"
                    px="10px"
                    py="5px"
                    alignItems="center"
                    color="white"
                  >
                    <Text fontSize="10px" pr="5px" fontWeight="900">
                      {String(item.percentage) + "%"}
                    </Text>
                    <GrLineChart size="12px" color="white" />
                  </Flex>

                  <Box>
                    <Heading textAlign="end" fontSize="25px" fontWeight="800">
                      {item.number}
                    </Heading>
                  </Box>
                </Flex>
              </Box>

              <Progress value={item.percentage} size="xs" colorScheme={item.color} mt="20px" />
            </GridItem>
          );
        })}
      </Grid>

      <Box p={"0 10px"} mt="2">
        <AdminDataTable
          ariaLabel="User analytics"
          columns={qParams === "subscribed" ? SUBSCRIPTIONCOLUMN : COLUMNS}
          data={Users}
          loading={isLoading}
          showRowNumbers
          startIndex={pageSize * currentPage + 1}
        />
      </Box>
      <Center>
        <Skeleton
          isLoaded={!isLoading}
          height="40px"
          width="60px"
          mt="1rem"
          startColor="#eeeeee"
          endColor="#f6f6f6"
          speed={1.1}
        >
          {!Users || Users.length === 0 ? (
            ""
          ) : (
            <Paginator
              pageCount={paginationMeta?.totalPages}
              onPageChange={(page) => {
                setCurrentPage(Number(page.selected));
                handlePageChange(currentPage);
              }}
              pageNo={paginationMeta?.currentPage}
              pageSize={pageSize}
              onSizeChange={(selectedPage) => {
                setPageSize(Number(selectedPage));
                handleSizeChange(pageSize);
              }}
            />
          )}
        </Skeleton>
      </Center>
      <UpgradeUserModal
        isOpen={isUpgradeModalOpen}
        onClose={closeUpgradeModal}
        userEmail={upgradeUserEmail}
        onUpgrade={handleUpgradeComplete}
      />
    </Container>
  );
};

export default RegisteredUsers;
